<script>
  import {
    imageReferences, addReferenceImage, removeReferenceImage,
    setReferenceEnabled, moveReferenceImage, setGuideStrength
  } from '../../state/image-references.svelte.js';
  import { imageModels } from '../../state/model-catalog.svelte.js';
  import { getImageInputCapabilities } from '../../utils/image-model-parameters.js';
  import ReferenceImageTile from './ReferenceImageTile.svelte';

  let { selectedIds = [], readonly = false, onMatchSize = null } = $props();
  const selectedModels = $derived(imageModels.filter((model) => selectedIds.includes(model.id)));
  const maxGuideStrength = $derived(Math.min(1, ...selectedModels.map(model => getImageInputCapabilities(model).maxGuideStrength)));

  async function handleFiles(event, role) {
    const files = [...event.currentTarget.files];
    event.currentTarget.value = '';
    for (const file of files) {
      try { await addReferenceImage(file, { role }); }
      catch { break; }
    }
  }

  function matchSize(asset) {
    const edge = Math.min(...selectedModels.map((model) => (
      model.parameters?.sizeConstraints?.maxEdge || model.parameters?.width?.max || 2048
    )), 3840);
    const scale = Math.min(1, edge / Math.max(asset.width, asset.height));
    onMatchSize?.({ width: Math.round(asset.width * scale / 16) * 16, height: Math.round(asset.height * scale / 16) * 16 });
  }
</script>

<div class="image-references-panel">
  <h4>Source images</h4>
  <p>Original files keep their pixels and order. References guide editing models; a guide image supplies an image-to-image starting point.</p>
  <div class="upload-actions">
    <label>+ Reference images<input type="file" accept="image/*,.heic,.heif" multiple disabled={readonly || imageReferences.isLoading} onchange={(event) => handleFiles(event, 'reference')} /></label>
    <label>+ Guide image<input type="file" accept="image/*,.heic,.heif" disabled={readonly || imageReferences.isLoading} onchange={(event) => handleFiles(event, 'guide')} /></label>
  </div>
  {#if imageReferences.isLoading}<p role="status">Reading original image…</p>{/if}
  {#if imageReferences.error}<p class="input-error" role="alert">{imageReferences.error}</p>{/if}
  {#if imageReferences.assets.length}
    <ol class="reference-list">
      {#each imageReferences.assets as asset, index (asset.id)}
        <li>
          <ReferenceImageTile {asset} label="Image {index + 1}" />
          <label class="enabled-control"><input type="checkbox" checked={asset.enabled !== false} disabled={readonly} onchange={(event) => setReferenceEnabled(asset.id, event.currentTarget.checked)} /> Use for generation</label>
          <div class="tile-actions">
            <button type="button" disabled={readonly || index === 0} aria-label="Move {asset.name} earlier" onclick={() => moveReferenceImage(asset.id, -1)}>↑</button>
            <button type="button" disabled={readonly || index === imageReferences.assets.length - 1} aria-label="Move {asset.name} later" onclick={() => moveReferenceImage(asset.id, 1)}>↓</button>
            {#if onMatchSize}<button type="button" disabled={readonly} onclick={() => matchSize(asset)}>Match shape</button>{/if}
            <button type="button" disabled={readonly} aria-label="Remove {asset.name}" onclick={() => removeReferenceImage(asset.id)}>Remove</button>
          </div>
        </li>
      {/each}
    </ol>
  {/if}
  {#if imageReferences.guideImage}
    <div class="guide-image">
      <ReferenceImageTile asset={imageReferences.guideImage} label="Guide image" />
      <label class="guide-strength">Guide influence: {imageReferences.guideStrength}
        <input type="range" min="0" max={maxGuideStrength} step="0.01" value={imageReferences.guideStrength} disabled={readonly} oninput={(event) => setGuideStrength(event.currentTarget.value)} />
      </label>
      {#if maxGuideStrength < 1}<p>FLUX.1 Schnell supports guide influence up to 99%.{imageReferences.guideStrength > maxGuideStrength ? ' Lower the saved influence before generating.' : ''}</p>{/if}
      <div class="tile-actions">
        {#if onMatchSize}<button type="button" disabled={readonly} onclick={() => matchSize(imageReferences.guideImage)}>Match shape</button>{/if}
        <button type="button" disabled={readonly} onclick={() => removeReferenceImage(imageReferences.guideImage.id)}>Remove guide</button>
      </div>
    </div>
  {/if}
  {#each selectedModels as model (model.id)}
    {@const capabilities = getImageInputCapabilities(model)}
    {#if capabilities.requiresReferences}<p>{model.label} requires 1–{capabilities.maxReferences} reference images.</p>{/if}
  {/each}
</div>

<style>
  .image-references-panel { display: grid; gap: 0.6rem; }
  h4 { margin: 0; font-size: 0.875rem; }
  p { margin: 0; font-size: 0.75rem; color: var(--color-text-muted); }
  .upload-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; }
  .upload-actions label { position: relative; display: flex; align-items: center; min-height: 46px; border: 1px solid var(--color-border); padding: 0.5rem; border-radius: 6px; font-size: 0.75rem; cursor: pointer; }
  .upload-actions input { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
  .upload-actions label:focus-within { outline: 2px solid var(--color-primary); }
  .reference-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
  .reference-list li, .guide-image { border: 1px solid var(--color-border); border-radius: 8px; padding: 0.5rem; display: grid; gap: 0.5rem; }
  .tile-actions { display: flex; flex-wrap: wrap; gap: 0.3rem; }
  button { min-width: 44px; min-height: 44px; border: 1px solid var(--color-border); background: var(--color-surface); color: var(--color-text); border-radius: 6px; padding: 0.35rem 0.5rem; font-size: 0.7rem; cursor: pointer; }
  button:disabled { opacity: 0.4; cursor: default; }
  .enabled-control, .guide-strength { min-height: 44px; font-size: 0.7rem; display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
  .enabled-control input { width: 20px; height: 20px; margin: 0; }
  .guide-strength input { width: 100%; min-height: 44px; }
  :is(button, input):focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
  .input-error { color: var(--color-error); }
</style>
